import { WorkItemStatus, WorkItemUserCategory } from '../__generated__/globalTypes';

import { calloutToAlertRow, ALERT_TONE_BY_TYPE } from './alertRow';

import type { IAlertRow, ListedAlert } from './alertRow';
import type { AlertRowStatus, AlertRowType } from './alerts';
import type { WorkItemDetailsFragment } from '../components/alerts/__generated__/workItemFragment.gql';

/** The fields a work-item alert row derives from — the whole fragment. */
export type AlertRowWorkItem = WorkItemDetailsFragment;

/**
 * A new `WorkItemUserCategory` does not compile until it picks a row type —
 * and with it the tone (`CONFIG` → error) and icon bucket the row maps
 * already assign that type.
 */
export const CALLOUT_TYPE_BY_WORK_ITEM_CATEGORY: Record<WorkItemUserCategory, AlertRowType> = {
  [WorkItemUserCategory.Update]: 'UPDATE',
  [WorkItemUserCategory.Reminder]: 'REMINDER',
  [WorkItemUserCategory.Config]: 'CONFIG',
};

/**
 * A work item's four-way row state folded from `(status, read)` — a new
 * `WorkItemStatus` does not compile until it picks one. Composed with
 * `ALERT_ITEM_STATUS_BY_CALLOUT_STATUS`, this is the badge table: ACTIVE+unread
 * → `unread`, ACTIVE+read → `read`, PENDING → `pending`, ARCHIVED → `resolved`.
 */
export const CALLOUT_STATUS_BY_WORK_ITEM_STATUS: Record<WorkItemStatus, (read: boolean) => AlertRowStatus> = {
  [WorkItemStatus.Active]: (read) => (read ? 'READ' : 'UNREAD'),
  [WorkItemStatus.Pending]: () => 'PENDING',
  [WorkItemStatus.Archived]: () => 'ARCHIVED',
};

/**
 * Whether a category's alert reads as an error rather than a warning. CONFIG is
 * the only one: it asks the user to fix something before the product works.
 */
export function isWorkItemAlertSevere(category: WorkItemUserCategory): boolean {
  return ALERT_TONE_BY_TYPE[CALLOUT_TYPE_BY_WORK_ITEM_CATEGORY[category]] === 'error';
}

/**
 * Whether the item is still new to the user: live work they have not opened.
 *
 * The same fold `CALLOUT_STATUS_BY_WORK_ITEM_STATUS` performs, named on its own
 * because a caller that only wants this one bit should not have to go through
 * the row shape to ask for it.
 */
export function isWorkItemUnread(workItem: Pick<AlertRowWorkItem, 'status' | 'read'>): boolean {
  return workItem.status === WorkItemStatus.Active && !workItem.read;
}

/**
 * A work item as the row every alert surface renders — the sole producer of
 * `ListedAlert` on the wire path, so the row model's unions carry exactly
 * what this fold emits.
 *
 * `subType` is always `null`, so `getAlertRowAction` answers `null` for a
 * CONFIG row — the work-item surfaces route CONFIG on the relation instead,
 * see `getWorkItemAlertRowAction`. The plural relations flatten first-of-array;
 * the nested shapes are the fragment contract in `workItemFragment.ts`.
 *
 * The timestamp reads `dueDate ?? createdAt` — a dated reminder shows when it
 * is due (`formatCompactTimeAgo` renders future instants), everything else
 * shows when it was raised.
 */
export function workItemToListedAlert(workItem: AlertRowWorkItem): ListedAlert {
  return {
    id: workItem.id,
    subType: null,
    type: CALLOUT_TYPE_BY_WORK_ITEM_CATEGORY[workItem.category],
    title: workItem.title,
    body: workItem.body,
    status: CALLOUT_STATUS_BY_WORK_ITEM_STATUS[workItem.status](workItem.read),
    eventDate: workItem.dueDate ?? workItem.createdAt,
    transaction: workItem.transactions[0] ?? null,
    merchant: workItem.merchants[0] ?? null,
    bill: workItem.bills[0] ?? null,
    financialAccount: workItem.financialAccount,
    bankConnection: workItem.bankConnection,
    recurringTransaction: workItem.recurringTransaction,
  };
}

/**
 * Turns a work item into the props an `AlertListItem` renders — the twin of
 * `calloutToAlertRow`, and implemented through it so the two paths cannot
 * derive a row differently.
 */
export const workItemToAlertRow = (workItem: AlertRowWorkItem, now?: Date, timeZone?: string): IAlertRow =>
  calloutToAlertRow(workItemToListedAlert(workItem), now, timeZone);
